import type { EquityDataRequestForAPIClient } from "@/interfaces/dataRequest/equity";
import ShowToaster from "@/toaster";
import {
  getRequestObjectForAPI,
  getRequestObjectForAPIForPrePost,
} from "@/utils/dataRequest/equity";
import {
  getRequestObjectForReputationAPI,
  getRequestObjectForReputationAPIForPrePost,
} from "@/utils/dataRequest/reputation";
import {
  getRequestObjectForVisibilityAPI,
  getRequestObjectForVisibilityAPIForPrePost,
} from "@/utils/dataRequest/visibility";
import { getPrevPeriodDateRange } from "@/utils/date";
import { apiInstances } from "@endpoints/config";
import {
  ADVERTISING_START_DATE,
  REPUTATION_START_DATE,
} from "@/constant/constantsThrougoutProject";

const dataService = {
  equity: {
    byBrand: {
      trend: async (requestData: EquityDataRequestForAPIClient) => {
        try {
          const requestPayload = getRequestObjectForAPI(requestData);
          const response = await apiInstances.data
            .post("trend/equity", {
              json: requestPayload,
            })
            .json();
          return { response, metaData: requestPayload };
        } catch (err: unknown) {
          return { error: "Something went wrong", errorCode: 500 };
        }
      },
      pointInTime: async (requestData: EquityDataRequestForAPIClient) => {
        try {
          const requestPayload = getRequestObjectForAPI(requestData);
          const response = await apiInstances.data
            .post("equity", {
              json: requestPayload,
            })
            .json();
          const { prevPeriodStartDate, prevPeriodEndDate } =
            getPrevPeriodDateRange({
              startDate: requestPayload.fromDate,
              endDate: requestPayload.toDate,
              statTest: requestPayload.statTesting,
            });
          return {
            response,
            metaData: {
              ...requestPayload,
              prevPeriodStartDate,
              prevPeriodEndDate,
            },
          };
        } catch (err: unknown) {
          return { error: "Something went wrong", errorCode: 500 };
        }
      },
      prePost: async (requestData: EquityDataRequestForAPIClient) => {
        try {
          const requestPayload = getRequestObjectForAPIForPrePost(requestData);
          const response = await apiInstances.data
            .post("prepost", {
              json: requestPayload,
            })
            .json();
          return { response, metaData: requestPayload };
        } catch (err: unknown) {
          return { error: "Something went wrong", errorCode: 500 };
        }
      },
    },
  },
  conversion: {
    byBrand: {
      trend: async (requestData: EquityDataRequestForAPIClient) => {
        try {
          const requestPayload = getRequestObjectForAPI(requestData);
          const response = await apiInstances.data
            .post("trend/conversion", {
              json: requestPayload,
            })
            .json();
          return { response, metaData: requestPayload };
        } catch (err: unknown) {
          return { error: "Something went wrong", errorCode: 500 };
        }
      },
      pointInTime: async (requestData: EquityDataRequestForAPIClient) => {
        try {
          const requestPayload = getRequestObjectForAPI(requestData);
          const response = await apiInstances.data
            .post("conversion", {
              json: requestPayload,
            })
            .json();
          const { prevPeriodStartDate, prevPeriodEndDate } =
            getPrevPeriodDateRange({
              startDate: requestPayload.fromDate,
              endDate: requestPayload.toDate,
              statTest: requestPayload.statTesting,
            });
          return {
            response,
            metaData: {
              ...requestPayload,
              prevPeriodStartDate,
              prevPeriodEndDate,
            },
          };
        } catch (err: unknown) {
          return { error: "Something went wrong", errorCode: 500 };
        }
      },
      prePost: async (requestData: EquityDataRequestForAPIClient) => {
        try {
          const requestPayload = getRequestObjectForAPIForPrePost(requestData);
          const response = await apiInstances.data
            .post("prepost", {
              json: requestPayload,
            })
            .json();
          return { response, metaData: requestPayload };
        } catch (err: unknown) {
          return { error: "Something went wrong", errorCode: 500 };
        }
      },
    },
  },
  emotional: {
    byBrand: {
      trend: async (requestData: EquityDataRequestForAPIClient) => {
        try {
          const requestPayload = getRequestObjectForAPI(requestData);
          const response = await apiInstances.data
            .post("trend/emotional", {
              json: requestPayload,
            })
            .json();
          return { response, metaData: requestPayload };
        } catch (err: unknown) {
          return { error: "Something went wrong", errorCode: 500 };
        }
      },
      pointInTime: async (requestData: EquityDataRequestForAPIClient) => {
        try {
          const requestPayload = getRequestObjectForAPI(requestData);
          const response = await apiInstances.data
            .post("emotional", {
              json: requestPayload,
            })
            .json();
          const { prevPeriodStartDate, prevPeriodEndDate } =
            getPrevPeriodDateRange({
              startDate: requestPayload.fromDate,
              endDate: requestPayload.toDate,
              statTest: requestPayload.statTesting,
            });
          return {
            response,
            metaData: {
              ...requestPayload,
              prevPeriodStartDate,
              prevPeriodEndDate,
            },
          };
        } catch (err: unknown) {
          return { error: "Something went wrong", errorCode: 500 };
        }
      },
      prePost: async (requestData: EquityDataRequestForAPIClient) => {
        try {
          const requestPayload = getRequestObjectForAPIForPrePost(requestData);
          const response = await apiInstances.data
            .post("prepost", {
              json: requestPayload,
            })
            .json();
          return { response, metaData: requestPayload };
        } catch (err: unknown) {
          return { error: "Something went wrong", errorCode: 500 };
        }
      },
    },
  },
  advertising: {
    byBrand: {
      trend: async (requestData: EquityDataRequestForAPIClient) => {
        try {
          const requestPayload = getRequestObjectForAPI(requestData);
          const response = await apiInstances.data
            .post("trend/advertising", {
              json: requestPayload,
            })
            .json();
          return { response, metaData: requestPayload };
        } catch (err: unknown) {
          return { error: "Something went wrong", errorCode: 500 };
        }
      },
      pointInTime: async (requestData: EquityDataRequestForAPIClient) => {
        try {
          const requestPayload = getRequestObjectForAPI(requestData);
          const response = await apiInstances.data
            .post("advertising", {
              json: requestPayload,
            })
            .json();
          const { prevPeriodStartDate, prevPeriodEndDate } =
            getPrevPeriodDateRange({
              startDate: requestPayload.fromDate,
              endDate: requestPayload.toDate,
              statTest: requestPayload.statTesting,
              restrictedDate: ADVERTISING_START_DATE,
            });
          return {
            response,
            metaData: {
              ...requestPayload,
              prevPeriodStartDate,
              prevPeriodEndDate,
            },
          };
        } catch (err: unknown) {
          return { error: "Something went wrong", errorCode: 500 };
        }
      },
      prePost: async (requestData: EquityDataRequestForAPIClient) => {
        try {
          const requestPayload = getRequestObjectForAPIForPrePost(requestData);
          const response = await apiInstances.data
            .post("prepost", {
              json: requestPayload,
            })
            .json();
          return { response, metaData: requestPayload };
        } catch (err: unknown) {
          return { error: "Something went wrong", errorCode: 500 };
        }
      },
      profile: async (requestData: EquityDataRequestForAPIClient) => {
        try {
          const requestPayload = getRequestObjectForAPI(requestData);
          const response = await apiInstances.data
            .post("profile", {
              json: requestPayload,
            })
            .json();
          return { response };
        } catch (err: unknown) {
          return { error: "Something went wrong", errorCode: 500 };
        }
      },
    },
  },
  visibility: {
    getSegmentData: async (requestPayload: any) => {
      try {
        if (requestPayload.viewMode === "TREND") {
          requestPayload = getRequestObjectForVisibilityAPI(requestPayload);
        } else if (requestPayload.viewMode === "POINT_IN_TIME") {
          requestPayload = getRequestObjectForVisibilityAPI(requestPayload);
        }
        const response = await apiInstances.data
          .post(`visibility/segment`, {
            json: requestPayload,
          })
          .json();
        const { prevPeriodStartDate, prevPeriodEndDate } =
          getPrevPeriodDateRange({
            startDate: requestPayload.fromDate,
            endDate: requestPayload.toDate,
            statTest: requestPayload.statTesting,
            restrictedDate: ADVERTISING_START_DATE,
          });
        return {
          response: response,
          metaData: {
            ...requestPayload,
            prevPeriodStartDate,
            prevPeriodEndDate,
          },
        };
        } catch (error: any) {
        if (error?.error === "INACCESSIBLE_MARKET") {
        } else {
        }
        return { error: "Something went wrong", errorCode: 500 };
      }
    },
    getBrandData: async (requestPayload: any) => {
      try {
        if (requestPayload.viewMode === "PRE_POST") {
          requestPayload =
            getRequestObjectForVisibilityAPIForPrePost(requestPayload);
        } else if (requestPayload.viewMode === "TREND") {
          requestPayload = getRequestObjectForVisibilityAPI(requestPayload);
        } else if (requestPayload.viewMode === "POINT_IN_TIME") {
          requestPayload = getRequestObjectForVisibilityAPI(requestPayload);
        }
        const response = await apiInstances.data
          .post(`visibility/brand`, {
            json: requestPayload,
          })
          .json();
        const prevDates = getPrevPeriodDateRange({
          startDate: requestPayload?.fromDate,
          endDate: requestPayload?.toDate,
          statTest: requestPayload?.statTesting,
          restrictedDate: ADVERTISING_START_DATE,
        });
        return {
          response: response,
          metaData: {
            ...requestPayload,
            prevPeriodStartDate: prevDates?.prevPeriodStartDate,
            prevPeriodEndDate: prevDates?.prevPeriodEndDate,
          },
        };
      } catch (error: any) {
        if (error?.error === "INACCESSIBLE_MARKET") {
        } else {
        }
        return { error: "Something went wrong", errorCode: 500 };
      }
    },
    getMarketData: async (requestPayload: any) => {
      try {
        if (requestPayload.viewMode === "TREND") {
          requestPayload = getRequestObjectForVisibilityAPI(requestPayload);
        } else if (requestPayload.viewMode === "POINT_IN_TIME") {
          requestPayload = getRequestObjectForVisibilityAPI(requestPayload);
        }
        const response = await apiInstances.data
          .post(`visibility/market`, {
            json: requestPayload,
          })
          .json();
        const { prevPeriodStartDate, prevPeriodEndDate } =
          getPrevPeriodDateRange({
            startDate: requestPayload.fromDate,
            endDate: requestPayload.toDate,
            statTest: requestPayload.statTesting,
            restrictedDate: ADVERTISING_START_DATE,
          });
        return {
          response: response,
          metaData: {
            ...requestPayload,
            prevPeriodStartDate,
            prevPeriodEndDate,
          },
        };
      } catch (error: unknown) {
        return { error: "Something went wrong", errorCode: 500 };
      }
    },
    getxlsxExport: async (requestPayload: any) => {
      try {
        const response = await apiInstances.export
          .post(`export/visibility/export?fileType=xlsx`, {
            json: requestPayload,
          })
          .text();
        return response;
      } catch (err: unknown) {
        return { error: "Something went wrong", errorCode: 500 };
      }
    },
    getpptExport: async (requestPayload: any) => {
      try {
        const response = await apiInstances.data
          .post(`dataexport/visibility/export?fileType=pptx`, {
            json: requestPayload,
          })
          .text();
        return response;
      } catch (err: unknown) {
        return { error: "Something went wrong", errorCode: 500 };
      }
    },
  },
  reputation: {
    getSegmentData: async (requestPayload: any) => {
      try {
        if (requestPayload.viewMode === "TREND") {
          requestPayload = getRequestObjectForReputationAPI(requestPayload);
        } else if (requestPayload.viewMode === "POINT_IN_TIME") {
          requestPayload = getRequestObjectForReputationAPI(requestPayload);
        }
        const response = await apiInstances.data
          .post(`rq/segment`, {
            json: requestPayload,
          })
          .json();
        const { prevPeriodStartDate, prevPeriodEndDate } =
          getPrevPeriodDateRange({
            startDate: requestPayload.fromDate,
            endDate: requestPayload.toDate,
            statTest: requestPayload.statTesting,
            restrictedDate: REPUTATION_START_DATE,
          });
        return {
          response: response,
          metaData: {
            ...requestPayload,
            prevPeriodStartDate,
            prevPeriodEndDate,
          },
        };
      } catch (error: any) {
        if (error?.error === "INACCESSIBLE_MARKET") {
        } else {
        }
        return { error: "Something went wrong", errorCode: 500 };
      }
    },
    getBrandData: async (requestPayload: any) => {
      try {
        if (requestPayload.viewMode === "PRE_POST") {
          requestPayload =
            getRequestObjectForReputationAPIForPrePost(requestPayload);
        } else if (requestPayload.viewMode === "TREND") {
          requestPayload = getRequestObjectForReputationAPI(requestPayload);
        } else if (requestPayload.viewMode === "POINT_IN_TIME") {
          requestPayload = getRequestObjectForReputationAPI(requestPayload);
        }
        const response = await apiInstances.data
          .post(`rq/brand`, {
            json: requestPayload,
          })
          .json();
        const prevDates = getPrevPeriodDateRange({
          startDate: requestPayload?.fromDate,
          endDate: requestPayload?.toDate,
          statTest: requestPayload?.statTesting,
          restrictedDate: REPUTATION_START_DATE,
        });
        return {
          response: response,
          metaData: {
            ...requestPayload,
            prevPeriodStartDate: prevDates?.prevPeriodStartDate,
            prevPeriodEndDate: prevDates?.prevPeriodEndDate,
          },
        };
      } catch (error: any) {
        if (error?.error === "INACCESSIBLE_MARKET") {
        } else {
        }
        return { error: "Something went wrong", errorCode: 500 };
      }
    },
    getMarketData: async (requestPayload: any) => {
      try {
        if (requestPayload.viewMode === "TREND") {
          requestPayload = getRequestObjectForReputationAPI(requestPayload);
        } else if (requestPayload.viewMode === "POINT_IN_TIME") {
          requestPayload = getRequestObjectForReputationAPI(requestPayload);
        }
        const response = await apiInstances.data
          .post(`rq/market`, {
            json: requestPayload,
          })
          .json();
        const { prevPeriodStartDate, prevPeriodEndDate } =
          getPrevPeriodDateRange({
            startDate: requestPayload.fromDate,
            endDate: requestPayload.toDate,
            statTest: requestPayload.statTesting,
            restrictedDate: REPUTATION_START_DATE,
          });
        return {
          response: response,
          metaData: {
            ...requestPayload,
            prevPeriodStartDate,
            prevPeriodEndDate,
          },
        };
      } catch (error: unknown) {
        return { error: "Something went wrong", errorCode: 500 };
      }
    },
    getxlsxExport: async (requestPayload: any) => {
      try {
        const response = await apiInstances.export
          .post(`export/RQ/export?fileType=xlsx`, {
            json: requestPayload,
          })
          .text();
        return response;
      } catch (err: unknown) {
        return { error: "Something went wrong", errorCode: 500 };
      }
    },
    getpptExport: async (requestPayload: any) => {
      try {
        const response = await apiInstances.data
          .post(`dataexport/RQ/export?fileType=pptx`, {
            json: requestPayload,
          })
          .text();
        return response;
      } catch (err: unknown) {
        return { error: "Something went wrong", errorCode: 500 };
      }
    },
  },
  profileFilters: {
    metric: async (geoIds: any) => {
      try {
        const response = await apiInstances.data
          .post("profile/filters", {
            json: geoIds,
          })
          .json();
        return response;
      } catch (err: unknown) {
        return { error: "Something went wrong", errorCode: 500 };
      }
    },
    response: async (requestPayload: any, metricId: any) => {
      try {
        const response = await apiInstances.data
          .post(`profile/filteroptions/${metricId}`, {
            json: requestPayload,
          })
          .json();
        return response;
      } catch (err: unknown) {
        return { error: "Something went wrong", errorCode: 500 };
      }
    },
  },
  questions: {
    getQuestionsList: async (userId: number) => {
      try {
        const response = await apiInstances.data
          .get(`questions/${userId}`)
          .json();
        return response;
      } catch (err: unknown) {
        return [];
      }
    },
    getSegmentData: async (requestPayload: any) => {
      try {
        const response = await apiInstances.data
          .post("questions/segment", {
            json: requestPayload,
          })
          .json();
        return response;
      } catch (error: unknown) {
        return { error: "Something went wrong", errorCode: 500 };
      }
    },
    getBrandData: async (requestPayload: any) => {
      try {
        const response = await apiInstances.data
          .post("questions/brand", {
            json: requestPayload,
          })
          .json();
        return response;
      } catch (error: unknown) {
        return { error: "Something went wrong", errorCode: 500 };
      }
    },
    createRollup: async (payload: any) => {
      try {
        const response = await apiInstances.data
          .post(`questions/rollups/save`, {
            json: payload,
          })
          .json();
        return response;
      } catch (err: unknown) {
        return { error: "Something went wrong", errorCode: 500 };
      }
    },
    updateQuestionOptionColor: async (
      questionGroup: string,
      optionId: number,
      color: string,
      entityType: "QUESTION_OPTION" | "ROLLUP" = "QUESTION_OPTION"
    ) => {
      try {
        const response = await apiInstances.data
          .put(`questions/customize/${questionGroup}`, {
            json: {
              color,
              entityId: optionId,
              entityType,
              sequence: 0,
            },
          })
          .text();
        return response === "Customization updated successfully."
          ? true
          : false;
      } catch (err: unknown) {
        return { error: "Something went wrong", errorCode: 500 };
      }
    },
  },
  getIngestionTime: async (markets: number[]) => {
    try {
      const response = await apiInstances.data
        .post("updated-time", {
          json: { geoIds: markets },
        })
        .json();
      return response;
      } catch (error: unknown) {
    }
  },
  export: {
    exclusiveQuestionSegmentPptx: async (requestPayload: any) => {
      try {
        const response = await apiInstances.data
          .post("segment/export?fileType=pptx", {
            json: requestPayload,
          })
          .text();
        return response;
      } catch (err: unknown) {
        return { error: "Something went wrong", errorCode: 500 };
      }
    },
    exclusiveQuestionSegmentxlsx: async (requestPayload: any) => {
      try {
        const response = await apiInstances.export
          .post("segment/export?fileType=xlsx", {
            json: requestPayload,
          })
          .text();
        return response;
      } catch (err: unknown) {
        return { error: "Something went wrong", errorCode: 500 };
      }
    },
    exclusiveQuestionBrandPptx: async (requestPayload: any) => {
      try {
        const response = await apiInstances.data
          .post("brand/export?fileType=pptx", {
            json: requestPayload,
          })
          .text();
        return response;
      } catch (err: unknown) {
        return { error: "Something went wrong", errorCode: 500 };
      }
    },
    exclusiveQuestionBrandxlsx: async (requestPayload: any) => {
      try {
        const response = await apiInstances.export
          .post("brand/export?fileType=xlsx", {
            json: requestPayload,
          })
          .text();
        return response;
      } catch (err: unknown) {
        return { error: "Something went wrong", errorCode: 500 };
      }
    },
    getxlsxExport: async (requestPayload: any, path: string) => {
      try {
        const response = await apiInstances.export
          .post(`${path}/export?fileType=xlsx`, {
            json: requestPayload,
          })
          .text();
        return response;
      } catch (err: unknown) {
        return { error: "Something went wrong", errorCode: 500 };
      }
    },
    getpptExport: async (requestPayload: any, path: string) => {
      try {
        const response = await apiInstances.export
          .post(`${path}/export?fileType=xlsx`, {
            json: requestPayload,
          })
          .text();
        return response;
      } catch (err: unknown) {
        return { error: "Something went wrong", errorCode: 500 };
      }
    },
  },
  butterfly: async (requestData: EquityDataRequestForAPIClient) => {
    try {
      const requestPayload = getRequestObjectForAPI(requestData);
      const response = await apiInstances.data
        .post("butterfly", {
          json: requestPayload,
        })
        .json();
      return { response, metaData: requestPayload };
      } catch (err: unknown) {
      return { error: "Something went wrong", errorCode: 500 };
    }
  },
  differentiation: async (requestData: EquityDataRequestForAPIClient) => {
    try {
      const requestPayload = getRequestObjectForAPI(requestData);
      const response: any = await apiInstances.data
        .post("da", {
          json: requestPayload,
        })
        .json();
      if (response?.length === 0) {
        return { error: "No data found", errorCode: 500 };
      }
      return { response, metaData: requestPayload };
      } catch (err: unknown) {
      return { error: "Something went wrong", errorCode: 500 };
    }
  },
  perceptual: async (requestData: EquityDataRequestForAPIClient) => {
    try {
      const requestPayload = getRequestObjectForAPI(requestData);
      const response: any = await apiInstances.data
        .post("ca", {
          json: requestPayload,
        })
        .json();
      if (response?.length === 0) {
        return { error: "No data found", errorCode: 500 };
      }
      return {
        response: response,
        metaData: requestPayload,
      };
      } catch (err: unknown) {
      return { error: "Something went wrong", errorCode: 500 };
    }
  },
  profile: async (requestData: EquityDataRequestForAPIClient) => {
    try {
      const requestPayload = getRequestObjectForAPI(requestData);
      const response = await apiInstances.data
        .post("profile", {
          json: requestPayload,
        })
        .json();
      return {
        response: response,
        metaData: requestPayload,
      };
      } catch (err: unknown) {
      return { error: "Something went wrong", errorCode: 500 };
    }
  },
};

export default dataService;
